/**
 * Billing Session Page (Phase 10a.4)
 *
 * Sequential billing flow: step through active projects, select unbilled items,
 * create invoices, and see a summary of what was billed.
 *
 * Three states:
 * 1. Loading - fetching billing queue
 * 2. Active Session - stepping through projects with item selection
 * 3. Summary - review of created invoices with links
 *
 * Gap fixes (v0.9.1):
 * - Tax rate read from tenant invoice defaults (GET /api/me/settings/invoice-defaults)
 * - Due date calculated from payment_terms setting
 * - Keyboard shortcuts: Enter = create invoice, Escape = skip
 * - Skipped projects tracked with reason and displayed in summary
 * - Tax rate and due date displayed in UI before creating invoice
 */
import { useState, useMemo, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { invalidateProjectListQueries } from '@/lib/cache-utils';
import { DashboardLayout } from '@/components/layout/DashboardLayout.tsx';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { ArrowLeft, SkipForward, CheckCircle2, FileText, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useBillingQueue } from '@/hooks/useInvoices';
import { api } from '@/lib/api';
import { getSuggestedTaxRate } from '@/lib/taxRates';
import { todayLocalDateString, addLocalDays } from '@/lib/localDate';
import type { BillingQueueProject, BillingQueueItem, InvoiceDefaults } from '@/lib/schemas';

const FALLBACK_TAX_RATE = 5.0;
const FALLBACK_TAX_LABEL = 'GST';
const FALLBACK_PAYMENT_TERMS = 30;

interface SelectedItem {
  id: string;
  description: string;
  amount: number; // editable amount (defaults to unbilled_amount)
  category: string | null;
  billable_item_id: string;
}

interface CreatedInvoice {
  id: string;
  invoice_number: string;
  project_name: string;
  project_number: string | null;
  fee_amount: number;
  tax_amount: number;
  total_amount: number;
}

interface SkippedProject {
  project_id: string;
  project_name: string;
  reason: 'user_skipped' | 'no_items_selected';
}

export function BillingSessionPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data, isLoading, error } = useBillingQueue();

  // Invoice defaults (tax rate, payment terms) — fetched once on mount
  const [invoiceDefaults, setInvoiceDefaults] = useState<InvoiceDefaults | null>(null);

  // Session state
  const [currentIndex, setCurrentIndex] = useState(0);
  const [createdInvoices, setCreatedInvoices] = useState<CreatedInvoice[]>([]);
  const [skippedProjects, setSkippedProjects] = useState<SkippedProject[]>([]);
  const [isCreating, setIsCreating] = useState(false);
  const [sessionComplete, setSessionComplete] = useState(false);

  // Per-project item selection state: { [itemId]: { checked, amount } }
  const [selectedItems, setSelectedItems] = useState<Record<string, { checked: boolean; amount: number }>>({});
  const [itemsInitialized, setItemsInitialized] = useState(false);

  // M2 (invoice-identity-cascade): a multi-client project emits ONE queue row per
  // client scope (billing_session.py AC8). Items carry only project_id, not a
  // scope, so every scope row of a project carries the SAME project-level item
  // list. Stepping through scope-1 bills those items; on scope-2 they're already
  // billed, and creating scope-2's invoice would hit the strict over-invoice guard
  // (invoice_items.py) → error + rollback. The guard backstops double-billing, but
  // Jamie sees a confusing scope-2 failure. Fix: after each successful create we
  // refetch the queue (below) so the next scope row reflects the now-billed items
  // (their unbilled_amount drops to 0 / they leave the queue), defaulting UNCHECKED
  // instead of error-rolling-back. Full per-scope item ALLOCATION remains a
  // follow-up; this only removes the error-rollback footgun.
  //
  // To keep the stepper order + "X of N" count + progress bar stable while the
  // LIVE queue shrinks under us (fully-billed rows drop out on refetch), we snapshot
  // the row identity order once when the queue first loads, then resolve each row's
  // CURRENT data from the live query by (project_id, client_scope_id). A row that
  // has fully billed out renders from its snapshot with an empty item list (nothing
  // left to bill), so the operator sees a clear "already billed" state, not an error.
  const liveProjects = data?.projects || [];
  const [sessionRows, setSessionRows] = useState<BillingQueueProject[] | null>(null);

  // Snapshot the row order exactly once, when the queue first resolves.
  if (sessionRows === null && data) {
    setSessionRows(liveProjects);
  }

  const rowKey = (p: { project_id: string; client_scope_id?: string | null }) =>
    `${p.project_id}::${p.client_scope_id ?? ''}`;

  const projects = sessionRows ?? liveProjects;

  // Resolve the row at currentIndex against the LIVE queue so its billable_items +
  // unbilled_total reflect the latest refetch. Fall back to the snapshot row with a
  // zeroed/empty live view when the row has fully billed out (no live match).
  const snapshotRow = projects[currentIndex] as BillingQueueProject | undefined;
  const currentProject: BillingQueueProject | undefined = useMemo(() => {
    if (!snapshotRow) return undefined;
    const live = liveProjects.find((p) => rowKey(p) === rowKey(snapshotRow));
    if (live) return live;
    // Fully billed out (or otherwise gone from the live queue): keep the row in the
    // stepper but show nothing left to bill, so Create is disabled and the operator
    // sees the items were already billed to the previous invoice.
    return { ...snapshotRow, billable_items: [], unbilled_total: 0 };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [snapshotRow, liveProjects]);

  // Fetch invoice defaults once on mount
  useEffect(() => {
    api.settings.getInvoiceDefaults()
      .then((defaults) => {
        setInvoiceDefaults(defaults);
      })
      .catch((err) => {
        console.warn('Failed to fetch invoice defaults, using defaults:', err);
        // Fall back to defaults: 5% GST, 30 days
      });
  }, []);

  // Initialize item selection when project changes
  const initializeItems = useCallback((project: BillingQueueProject) => {
    const initial: Record<string, { checked: boolean; amount: number }> = {};
    for (const item of project.billable_items) {
      initial[item.id] = { checked: true, amount: item.unbilled_amount };
    }
    setSelectedItems(initial);
    setItemsInitialized(true);
  }, []);

  // When currentProject changes, re-initialize items
  if (currentProject && !itemsInitialized) {
    initializeItems(currentProject);
  }

  // Calculate selected totals (only items with positive amounts count)
  const taxRate = invoiceDefaults?.tax_rate ?? FALLBACK_TAX_RATE;

  // v0.14.4 R1: header tax must match the per-line tax (category-aware), so the
  // invoice header total isn't overstated when the batch mixes revenue and
  // non-revenue (billable_expense / pass_through) lines. Tax is summed per item
  // using the category rate, not a flat rate over the whole fee.
  const selectedTotals = useMemo(() => {
    let feeAmount = 0;
    let taxAmount = 0;
    let count = 0;
    for (const [itemId, item] of Object.entries(selectedItems)) {
      if (item.checked && item.amount > 0) {
        feeAmount += item.amount;
        const billableItem = currentProject?.billable_items.find(bi => bi.id === itemId);
        const lineRate = getSuggestedTaxRate(billableItem?.category, taxRate);
        taxAmount += Math.round(item.amount * lineRate) / 100;
        count++;
      }
    }
    return {
      feeAmount,
      taxAmount,
      totalAmount: feeAmount + taxAmount,
      count,
    };
  }, [selectedItems, taxRate, currentProject]);

  const toggleItem = (itemId: string) => {
    setSelectedItems(prev => ({
      ...prev,
      [itemId]: { ...prev[itemId], checked: !prev[itemId]?.checked },
    }));
  };

  const updateItemAmount = (itemId: string, rawAmount: number) => {
    // Find the item's max unbilled amount
    const item = currentProject?.billable_items.find(bi => bi.id === itemId);
    const maxAmount = item?.unbilled_amount ?? 0;
    // Clamp: no negatives, no exceeding unbilled amount
    const amount = Math.min(Math.max(0, rawAmount), maxAmount);
    setSelectedItems(prev => ({
      ...prev,
      [itemId]: { ...prev[itemId], amount },
    }));
  };

  const advanceToNext = useCallback(() => {
    const nextIndex = currentIndex + 1;
    if (nextIndex >= projects.length) {
      setSessionComplete(true);
    } else {
      setCurrentIndex(nextIndex);
      setItemsInitialized(false);
    }
  }, [currentIndex, projects.length]);

  const handleSkip = useCallback(() => {
    if (currentProject) {
      setSkippedProjects(prev => [...prev, {
        project_id: currentProject.project_id,
        project_name: currentProject.project_name,
        reason: 'user_skipped',
      }]);
    }
    advanceToNext();
  }, [currentProject, advanceToNext]);

  // Keyboard shortcuts: Enter = create invoice, Escape = skip
  useEffect(() => {
    if (sessionComplete) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      // Don't trigger when user is typing in an input or textarea
      const tag = (document.activeElement as HTMLElement)?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA') return;

      if (event.key === 'Enter' && !isCreating) {
        event.preventDefault();
        if (selectedTotals.count > 0) {
          handleCreateInvoice();
        }
      } else if (event.key === 'Escape') {
        event.preventDefault();
        handleSkip();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTotals.count, isCreating, sessionComplete, handleSkip]);

  // Calculate due date string from invoice date + payment terms
  const getDueDate = (invoiceDateStr: string): string => {
    const paymentTerms = invoiceDefaults?.payment_terms ?? FALLBACK_PAYMENT_TERMS;
    // Calendar-based addition (addLocalDays uses setDate, not millisecond
    // arithmetic) -- adding N*24h in ms drifts by the DST offset whenever
    // the invoice-date-to-due-date span crosses a spring-forward/fall-back
    // boundary, which silently landed on the wrong calendar day here.
    return addLocalDays(invoiceDateStr, paymentTerms) ?? invoiceDateStr;
  };

  const handleCreateInvoice = async () => {
    if (!currentProject || selectedTotals.count === 0) return;

    setIsCreating(true);
    let pendingInvoiceId: string | null = null;
    try {
      // 1. Get next invoice number
      const nextNumberResp = await api.invoices.getNextNumber();
      const invoiceNumber = nextNumberResp.suggested_invoice_number;

      // 2. Create the invoice (use local date to avoid UTC next-day issues)
      const today = todayLocalDateString();
      const dueDate = getDueDate(today);

      const invoiceResp = await api.invoices.create({
        invoice_number: invoiceNumber,
        project_id: currentProject.project_id,
        organization_id: currentProject.organization_id!,
        // R2 (invoice-identity-cascade) AC7: pass the resolved client_scope_id
        // the queue row carries (deterministic primary rule). This is the root-
        // cause fix for the Dana Rocca / 0609 bug -- the batch create previously
        // omitted client_scope_id, so batch-created invoices got no scope link
        // and inherited nothing, falling back to the org legal name. With the
        // scope id, the server resolves the same identity bundle a single-created
        // invoice would. Jamie-invisible (AC12): no new input -- it rides the
        // queue row he already steps through.
        client_scope_id: currentProject.client_scope_id ?? undefined,
        invoice_date: today,
        due_date: dueDate,
        fee_amount: selectedTotals.feeAmount,
        tax_amount: selectedTotals.taxAmount,
        total_amount: selectedTotals.totalAmount,
        status: 'draft',
      });

      const invoice = invoiceResp.invoice;
      // Track ID so we can roll back if item creation fails
      pendingInvoiceId = invoice.id;

      // 3. Add invoice items for each selected billable item.
      // v0.14.4 R1: derive tax_rate per item from its category via the shared
      // util (0% for billable_expense / pass_through, tenant default otherwise)
      // instead of applying the flat tenant default to every line — the
      // category-ignoring bulk-create was the same defect class as invoice 0592.
      // R2 (bc-pst): the tenant rate flows THROUGH the shared util so all
      // three creation paths derive it identically.
      const tenantTaxRate = invoiceDefaults?.tax_rate ?? FALLBACK_TAX_RATE;
      const itemPromises = Object.entries(selectedItems)
        .filter(([, item]) => item.checked)
        .map(([itemId, item]) => {
          // Look up the billable item details from the current project
          const billableItem = currentProject.billable_items.find(bi => bi.id === itemId);
          // Revenue categories use the tenant rate; non-revenue → 0%.
          const taxRate = getSuggestedTaxRate(billableItem?.category, tenantTaxRate);
          const taxAmount = Math.round(item.amount * taxRate) / 100;

          return api.invoiceItems.create({
            invoice_id: invoice.id,
            billable_item_id: itemId,
            project_id: currentProject.project_id,
            description: billableItem?.description || 'Billable item',
            amount: item.amount,
            tax_rate: taxRate,
            tax_amount: taxAmount,
          });
        });

      await Promise.all(itemPromises);
      pendingInvoiceId = null; // Items succeeded — invoice is complete

      // Track the created invoice
      setCreatedInvoices(prev => [...prev, {
        id: invoice.id,
        invoice_number: invoice.invoice_number,
        project_name: currentProject.project_name,
        project_number: currentProject.project_number,
        fee_amount: selectedTotals.feeAmount,
        tax_amount: selectedTotals.taxAmount,
        total_amount: selectedTotals.totalAmount,
      }]);

      toast.success(`Invoice ${invoiceNumber} created`);

      // M2: refetch the billing queue BEFORE advancing so the next scope row of a
      // multi-client project reflects the items just billed (their unbilled_amount
      // drops to 0 / they leave the queue → default UNCHECKED) instead of carrying
      // the stale full list and error-rolling-back against the over-invoice guard.
      // The queue query is active and staleTime:0, so invalidate triggers a refetch;
      // we await it so the live unbilled_amounts are settled before the stepper
      // re-resolves the next row. Single-scope (the common) flow is unaffected — the
      // refetch is a no-op for the last/only row, then advanceToNext finishes.
      await queryClient.invalidateQueries({ queryKey: ['invoices', 'billing-queue'] });

      advanceToNext();
    } catch (err: any) {
      // Roll back the invoice header if items failed to create
      if (pendingInvoiceId) {
        try {
          await api.invoices.delete(pendingInvoiceId);
        } catch {
          // Rollback failed — invoice exists without items. Log and surface to user.
          console.error('Invoice rollback failed. Invoice ID:', pendingInvoiceId);
          toast.error('Invoice creation failed and could not be rolled back. Please delete the empty invoice manually.');
          return;
        }
      }
      toast.error(err.message || 'Failed to create invoice');
    } finally {
      setIsCreating(false);
    }
  };

  // Invalidate caches when session completes
  const handleDone = () => {
    queryClient.invalidateQueries({ queryKey: ['invoices'] });
    invalidateProjectListQueries(queryClient);
    queryClient.invalidateQueries({
      predicate: (query) => query.queryKey[0] === 'billable-items'
    });
    navigate('/invoices');
  };

  // --- Loading State ---
  if (isLoading) {
    return (
      <DashboardLayout title="Billing Session">
        <div className="space-y-3">
          {[...Array(5)].map((_, i) => (
            <Skeleton key={i} className="h-16 w-full" />
          ))}
        </div>
      </DashboardLayout>
    );
  }

  // --- Error State ---
  if (error) {
    return (
      <DashboardLayout title="Billing Session">
        <div className="rounded-lg border border-destructive bg-destructive/10 p-4">
          <p className="text-sm text-destructive">
            Failed to load billing queue: {(error as Error).message}
          </p>
        </div>
      </DashboardLayout>
    );
  }

  // --- Empty State ---
  if (projects.length === 0) {
    return (
      <DashboardLayout title="Billing Session">
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <CheckCircle2 className="h-12 w-12 text-muted-foreground mb-4" />
          <h2 className="text-lg font-semibold mb-2">All caught up!</h2>
          <p className="text-muted-foreground mb-6">
            No projects with unbilled items found.
          </p>
          <Button variant="outline" onClick={() => navigate('/invoices')}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Invoices
          </Button>
        </div>
      </DashboardLayout>
    );
  }

  // --- Summary State ---
  if (sessionComplete) {
    const totalInvoiced = createdInvoices.reduce((sum, inv) => sum + inv.total_amount, 0);

    return (
      <DashboardLayout title="Billing Session Complete">
        <div className="space-y-6">
          <div className="flex items-center gap-3">
            <CheckCircle2 className="h-6 w-6 text-green-600" />
            <div>
              <h2 className="text-lg font-semibold">Session Complete</h2>
              <p className="text-muted-foreground">
                Reviewed {projects.length} project{projects.length !== 1 ? 's' : ''} &middot;{' '}
                Created {createdInvoices.length} invoice{createdInvoices.length !== 1 ? 's' : ''} &middot;{' '}
                Total: ${totalInvoiced.toLocaleString('en-CA', { minimumFractionDigits: 2 })}
              </p>
            </div>
          </div>

          {createdInvoices.length > 0 && (
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Invoice #</TableHead>
                    <TableHead>Project</TableHead>
                    <TableHead className="text-right">Fee</TableHead>
                    <TableHead className="text-right">Tax</TableHead>
                    <TableHead className="text-right">Total</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {createdInvoices.map(inv => (
                    <TableRow
                      key={inv.id}
                      className="cursor-pointer hover:bg-muted/50"
                      onClick={() => navigate(`/invoices/${inv.id}`)}
                    >
                      <TableCell className="font-mono">{inv.invoice_number}</TableCell>
                      <TableCell>
                        {inv.project_number && (
                          <span className="text-muted-foreground mr-2">{inv.project_number}</span>
                        )}
                        {inv.project_name}
                      </TableCell>
                      <TableCell className="text-right">
                        ${inv.fee_amount.toLocaleString('en-CA', { minimumFractionDigits: 2 })}
                      </TableCell>
                      <TableCell className="text-right">
                        ${inv.tax_amount.toLocaleString('en-CA', { minimumFractionDigits: 2 })}
                      </TableCell>
                      <TableCell className="text-right font-medium">
                        ${inv.total_amount.toLocaleString('en-CA', { minimumFractionDigits: 2 })}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}

          {createdInvoices.length === 0 && (
            <p className="text-muted-foreground text-center py-8">
              No invoices were created during this session.
            </p>
          )}

          {skippedProjects.length > 0 && (
            <div className="rounded-lg border border-yellow-200 bg-yellow-50 p-4">
              <h3 className="font-semibold text-yellow-900">Skipped Projects ({skippedProjects.length})</h3>
              <ul className="mt-3 space-y-2">
                {skippedProjects.map(sp => (
                  <li key={sp.project_id} className="flex items-center justify-between text-sm text-yellow-800">
                    <span>{sp.project_name}</span>
                    <span className="text-xs text-yellow-700">
                      {sp.reason === 'user_skipped' ? 'Skipped by user' : 'No items selected'}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="flex justify-end">
            <Button onClick={handleDone}>
              Done
            </Button>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  // --- Active Session State ---
  // Compute due date for display
  const todayStr = todayLocalDateString();
  const displayDueDate = getDueDate(todayStr);
  const taxLabel = invoiceDefaults?.tax_label ?? FALLBACK_TAX_LABEL;
  const paymentTerms = invoiceDefaults?.payment_terms ?? FALLBACK_PAYMENT_TERMS;

  return (
    <DashboardLayout
      title="Billing Session"
      actions={
        <span className="text-sm text-muted-foreground">
          {currentIndex + 1} of {projects.length} projects
        </span>
      }
    >
      <div className="space-y-6">
        {/* Project Header */}
        <div className="flex items-start justify-between">
          <div>
            <div className="flex items-center gap-2">
              {currentProject!.project_number && (
                <span className="font-mono text-muted-foreground">
                  {currentProject!.project_number}
                </span>
              )}
              <h2 className="text-lg font-semibold">{currentProject!.project_name}</h2>
            </div>
            {currentProject!.client_name && (
              <p className="text-sm text-muted-foreground mt-1">
                Client: {currentProject!.client_name}
              </p>
            )}
            <p className="text-sm text-muted-foreground">
              Unbilled: ${currentProject!.unbilled_total.toLocaleString('en-CA', { minimumFractionDigits: 2 })}
            </p>
          </div>
          <Button variant="ghost" onClick={handleSkip} disabled={isCreating}>
            Skip
            <SkipForward className="ml-2 h-4 w-4" />
          </Button>
        </div>

        {/* M2: already-billed notice. On a multi-client project, items are not
            scope-partitioned — billing the previous scope's invoice consumed the
            shared item list, so this scope row has nothing left to bill. Surface a
            clear message instead of an empty table + silent over-invoice rollback. */}
        {currentProject!.billable_items.length === 0 && (
          <div
            className="rounded border border-yellow-200 bg-yellow-50 p-3 text-sm text-yellow-900"
            data-testid="already-billed-notice"
          >
            These items were already billed to the previous invoice. There's nothing
            left to bill for this client on this project — skip to continue.
          </div>
        )}

        {/* Billable Items */}
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-10"></TableHead>
                <TableHead>Description</TableHead>
                <TableHead className="text-right w-32">Amount</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {currentProject!.billable_items.map((item: BillingQueueItem) => {
                const selection = selectedItems[item.id];
                const isChecked = selection?.checked ?? false;
                const amount = selection?.amount ?? item.unbilled_amount;

                return (
                  <TableRow key={item.id} className={!isChecked ? 'opacity-50' : ''}>
                    <TableCell>
                      <Checkbox
                        checked={isChecked}
                        onCheckedChange={() => toggleItem(item.id)}
                        disabled={isCreating}
                        aria-label={`Include ${item.description}`}
                      />
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-col">
                        <span>{item.description}</span>
                        {item.invoiced_amount > 0 && (
                          <span className="text-xs text-muted-foreground">
                            Previously invoiced: ${item.invoiced_amount.toLocaleString('en-CA', { minimumFractionDigits: 2 })} of ${item.total_amount.toLocaleString('en-CA', { minimumFractionDigits: 2 })}
                          </span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <Input
                        type="number"
                        step="0.01"
                        min="0.01"
                        max={item.unbilled_amount}
                        value={amount}
                        onChange={(e) => updateItemAmount(item.id, parseFloat(e.target.value) || 0)}
                        onBlur={(e) => {
                          // Enforce minimum on blur (user finished editing)
                          const val = parseFloat(e.target.value) || 0;
                          if (val <= 0 && selectedItems[item.id]?.checked) {
                            updateItemAmount(item.id, item.unbilled_amount);
                          }
                        }}
                        disabled={!isChecked || isCreating}
                        className="w-28 text-right ml-auto"
                        aria-label={`Amount for ${item.description}`}
                      />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>

        {/* Invoice settings info: tax rate and due date */}
        <div className="rounded bg-blue-50 p-3 text-sm text-blue-900" data-testid="invoice-settings-info">
          <p>Tax rate: {taxLabel} {taxRate}%</p>
          <p>Due date: {displayDueDate} (Net {paymentTerms})</p>
        </div>

        {/* Totals + Action */}
        <div className="flex items-center justify-between border-t pt-4">
          <div className="text-sm">
            <p>
              Selected: <span className="font-medium">${selectedTotals.feeAmount.toLocaleString('en-CA', { minimumFractionDigits: 2 })}</span>
              {' + '}
              <span className="text-muted-foreground">${selectedTotals.taxAmount.toLocaleString('en-CA', { minimumFractionDigits: 2 })} {taxLabel}</span>
              {' = '}
              <span className="font-semibold">${selectedTotals.totalAmount.toLocaleString('en-CA', { minimumFractionDigits: 2 })}</span>
            </p>
          </div>
          <Button
            onClick={handleCreateInvoice}
            disabled={selectedTotals.count === 0 || isCreating}
          >
            {isCreating ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Creating...
              </>
            ) : (
              <>
                <FileText className="mr-2 h-4 w-4" />
                Create Invoice & Continue
              </>
            )}
          </Button>
        </div>

        {/* Progress indicator */}
        <div className="flex gap-1" role="progressbar" aria-label={`Project ${currentIndex + 1} of ${projects.length}`} aria-valuenow={currentIndex + 1} aria-valuemin={1} aria-valuemax={projects.length}>
          {projects.map((_, idx) => (
            <div
              key={idx}
              className={`h-1.5 flex-1 rounded-full ${
                idx < currentIndex
                  ? 'bg-green-500'
                  : idx === currentIndex
                  ? 'bg-primary'
                  : 'bg-muted'
              }`}
            />
          ))}
        </div>
      </div>
    </DashboardLayout>
  );
}
